import type { NetMessage } from './transport';

type SendFn = (msg: NetMessage) => void;

export interface ClockPing extends NetMessage {
  type: 'clock-ping';
  t0: number;
}
export interface ClockPong extends NetMessage {
  type: 'clock-pong';
  t0: number;
  hostTime: number;
}

/** Client side: estimates the offset between local clock and host clock. */
export class ClockSyncClient {
  private offset = 0;
  private samples: number[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private send: SendFn,
    private now: () => number,
  ) {}

  start(intervalMs = 1000): void {
    this.timer = setInterval(() => {
      this.send({ type: 'clock-ping', t0: this.now() });
    }, intervalMs);
  }
  /** Call when a clock-pong arrives. */
  onPong(pong: ClockPong): void {
    const t1 = this.now();
    const rtt = t1 - pong.t0;
    const estimatedHostNow = pong.hostTime + rtt / 2;
    const sample = estimatedHostNow - t1;
    this.samples.push(sample);
    if (this.samples.length > 10) {
      this.samples.shift();
    }
    this.offset = this.samples.reduce((a, b) => a + b, 0) / this.samples.length;
  }
  /** Estimated host clock = local now + offset. */
  hostNow(): number {
    return this.now() + this.offset;
  }
  getOffset(): number {
    return this.offset;
  }
  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
    this.timer = null;
  }
}

/** Host side: answers a ping with its own clock. */
export function makeClockPong(ping: ClockPing, hostNow: number): ClockPong {
  return { type: 'clock-pong', t0: ping.t0, hostTime: hostNow };
}
